import { z } from "zod";

/**
 * Whether a principal represents a real user or the internal system. Shared so
 * the backend enforces and payloads validate against the same constants.
 */
export const PrincipalKind = {
  USER: "user",
  SYSTEM: "system",
} as const;

/** A single principal kind value, e.g. "user". */
export type PrincipalKind = (typeof PrincipalKind)[keyof typeof PrincipalKind];

/** Zod schema validating a principal kind string against the known values. */
export const PrincipalKindSchema = z.enum(PrincipalKind);

/** Who is performing the operation on the principal's behalf. */
export const ActorType = {
  AGENT: "agent",
  USER: "user",
} as const;

/** A single actor type value, e.g. "agent". */
export type ActorType = (typeof ActorType)[keyof typeof ActorType];

/** Zod schema validating an actor type string against the known values. */
export const ActorTypeSchema = z.enum(ActorType);

/** The acting entity behind an operation, carrying the id its type implies. */
export const PrincipalActorSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal(ActorType.AGENT), threadId: z.string() }),
  z.object({ type: z.literal(ActorType.USER), userId: z.string() }),
]);

/** Who an operation is being performed on behalf of. */
export type PrincipalActor = z.infer<typeof PrincipalActorSchema>;
